<script>
import { Bar } from "vue-chartjs";

export default {
  extends: Bar,
  name: "CountChart",
  props: {
    chartData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      processedData: null,
      updateTimeout: null,
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        title: {
          display: true,
          text: "Success Rates by Sections(Count)",
          fontColor: "#ffffff",
          fontSize: 16,
          fontStyle: "bold",
        },
        legend: {
          position: "right",
          labels: {
            fontColor: "#ffffff",
            usePointStyle: true,
            padding: 20,
          },
        },
        scales: {
          xAxes: [
            {
              stacked: true,
              gridLines: {
                color: "#444444",
                drawBorder: false,
              },
              ticks: {
                fontColor: "#ffffff",
                fontSize: 12,
              },
            },
          ],
          yAxes: [
            {
              stacked: true,
              ticks: {
                beginAtZero: true,
                fontColor: "#ffffff",
                fontSize: 12,
              },
              gridLines: {
                color: "#444444",
                drawBorder: false,
              },
            },
          ],
        },
        layout: {
          padding: {
            top: 20,
            right: 20,
            bottom: 20,
            left: 20,
          },
        },
        animation: {
          duration: 0,
        },
      },
    };
  },
  mounted() {
    this.$nextTick(() => {
      // DOM element'in hazır olduğundan emin ol
      if (this.$el) {
        this.initChart();
      }
    });
  },

  activated() {
    // Tab'a geri dönüldüğünde chart'ı yeniden oluştur
    this.$nextTick(() => {
      if (!this.$data._chart && this.$el) {
        this.initChart();
      }
    });
  },

  deactivated() {
    // Tab'dan ayrılırken chart'ı temizle
    this.destroyChart();
  },

  beforeDestroy() {
    this.destroyChart();
  },
  watch: {
    chartData: {
      deep: true,
      immediate: false,
      handler(newVal, oldVal) {
        // Only update if data actually changed
        if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
          this.updateChart();
        }
      },
    },
  },
  methods: {
    initChart() {
      this.processedData = this.processChartData();
      this.renderChart(this.processedData, this.chartOptions);
    },

    destroyChart() {
      if (this.updateTimeout) {
        clearTimeout(this.updateTimeout);
        this.updateTimeout = null;
      }
      if (this.$data._chart) {
        this.$data._chart.destroy();
        this.$data._chart = null;
      }
    },

    updateChart() {
      // Debounce chart updates to prevent flickering
      if (this.updateTimeout) {
        clearTimeout(this.updateTimeout);
      }

      this.updateTimeout = setTimeout(() => {
        this.$nextTick(() => {
          // Her zaman chart'ı yeniden oluştur - data değişikliklerinde daha güvenilir
          this.destroyChart();
          this.initChart();
        });
      }, 100);
    },

    processChartData() {
      if (
        !this.chartData ||
        !this.chartData.data ||
        this.chartData.data.length === 0
      ) {
        // Fallback: show zero counts for common statuses (normalized)
        const normalizeStatus = (val) => (val ? String(val).trim().toLowerCase() : "unknown");
        const canonicalLabels = {
          "exceeds expectations": "Exceeds Expectations",
          "meets expectations": "Meets Expectations",
          "training needs identified": "Training Needs Identified",
          failed: "Failed",
          passed: "Passed",
          completed: "Completed",
          pending: "Pending",
          unknown: "Unknown",
        };
        const titleCase = (s) =>
          String(s)
            .replace(/\s+/g, " ")
            .split(" ")
            .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
            .join(" ");
        const displayLabelForStatus = (key) => canonicalLabels[key] || titleCase(key);
        const predefinedColors = {
          "exceeds expectations": "#17a2b8",
          "meets expectations": "#28a745",
          "training needs identified": "#6f42c1",
          failed: "#dc3545",
          passed: "#fd7e14",
          completed: "#20c997",
          pending: "#ffc107",
          unknown: "#343a40",
        };
        const fallbackPalette = [
          "#007bff",
          "#6610f2",
          "#e83e8c",
          "#fd7e14",
          "#28a745",
          "#20c997",
          "#17a2b8",
          "#6f42c1",
          "#dc3545",
          "#343a40",
        ];
        let paletteIndex = 0;
        const colorForStatus = (key) => {
          if (predefinedColors[key]) return predefinedColors[key];
          const color = fallbackPalette[paletteIndex % fallbackPalette.length];
          paletteIndex++;
          return color;
        };
        const fallbackStatusKeys = [
          "exceeds expectations",
          "meets expectations",
          "training needs identified",
          "failed",
          "passed",
        ];
        return {
          labels: ["No Data"],
          datasets: fallbackStatusKeys.map((key) => {
            const baseColor = colorForStatus(key);
            return {
              label: displayLabelForStatus(key),
              data: [0],
              backgroundColor: baseColor,
              borderColor: baseColor,
              borderWidth: 1,
              hoverBackgroundColor: baseColor,
              hoverBorderColor: baseColor,
            };
          }),
        };
      }
      // Dynamic status aggregation per section (raw counts)
      const sectionStats = {}; // { sectionName: { total, statusCounts: { normalizedKey: count } } }
      const allStatusesSet = new Set();

      const normalizeStatus = (val) => (val ? String(val).trim().toLowerCase() : "unknown");
      const canonicalLabels = {
        "exceeds expectations": "Exceeds Expectations",
        "meets expectations": "Meets Expectations",
        "training needs identified": "Training Needs Identified",
        failed: "Failed",
        passed: "Passed",
        completed: "Completed",
        pending: "Pending",
        unknown: "Unknown",
      };
      const titleCase = (s) =>
        String(s)
          .replace(/\s+/g, " ")
          .split(" ")
          .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
          .join(" ");
      const displayLabelForStatus = (key) => canonicalLabels[key] || titleCase(key);
      const predefinedColors = {
        "exceeds expectations": "#17a2b8",
        "meets expectations": "#28a745",
        "training needs identified": "#6f42c1",
        failed: "#dc3545",
        passed: "#fd7e14",
        completed: "#20c997",
        pending: "#ffc107",
        unknown: "#343a40",
      };
      const fallbackPalette = [
        "#007bff",
        "#6610f2",
        "#e83e8c",
        "#fd7e14",
        "#28a745",
        "#20c997",
        "#17a2b8",
        "#6f42c1",
        "#dc3545",
        "#343a40",
      ];
      let paletteIndex = 0;
      const colorForStatus = (key) => {
        if (predefinedColors[key]) return predefinedColors[key];
        const color = fallbackPalette[paletteIndex % fallbackPalette.length];
        paletteIndex++;
        return color;
      };

      this.chartData.data.forEach((group) => {
        const sectionName = group.group_name || "Unknown Section";
        const statusKey = normalizeStatus(group.status);
        allStatusesSet.add(statusKey);

        if (!sectionStats[sectionName]) {
          sectionStats[sectionName] = {
            total: 0,
            statusCounts: {},
          };
        }

        sectionStats[sectionName].total++;
        sectionStats[sectionName].statusCounts[statusKey] =
          (sectionStats[sectionName].statusCounts[statusKey] || 0) + 1;
      });

      const labels = Object.keys(sectionStats);
      const allStatuses = Array.from(allStatusesSet);

      const datasets = allStatuses.map((key) => {
        const data = labels.map((section) => {
          const stats = sectionStats[section];
          return stats.statusCounts[key] || 0;
        });
        const baseColor = colorForStatus(key);
        return {
          label: displayLabelForStatus(key),
          data,
          backgroundColor: baseColor,
          borderColor: baseColor,
          borderWidth: 1,
          hoverBackgroundColor: baseColor,
          hoverBorderColor: baseColor,
        };
      });

      return {
        labels,
        datasets,
      };
    },
  },
};
</script>
